// The page-wide Filter button (docs/app.md §5) — lives in the header among the
// queue tools. The button shows how many filter groups are active (and goes
// brand-colored); the popover holds the four groups — company, vendor, date
// range, total range — plus Clear all. Queue screens filter their loaded rows in
// memory (lib/filters.ts); Archives sends the same filter to the API as named
// parameters. The company and vendor option lists are derived by the parent from
// the rows behind the timeline badges, so they are naturally limited to what the
// user may see and never the whole vendor master. Each option carries the id as
// well as the name, because the API filters on the id.
import {
  Button,
  Dropdown,
  Field,
  Input,
  Option,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import { DismissRegular, FilterRegular } from '@fluentui/react-icons'
import { type InvoiceFilter, emptyFilter } from '../lib/filters'

const useStyles = makeStyles({
  surface: {
    width: '360px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  // Fluent's Dropdown defaults to a ~250px min-width; clear it so the dropdown
  // fits the popover instead of overflowing it.
  control: { width: '100%', minWidth: 0 },
  // Grid (not flex) so the two inputs are forced to equal tracks and can never
  // outgrow the popover, whatever their intrinsic width.
  rangePair: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
    alignItems: 'center',
    gap: '8px',
  },
  rangeInput: { minWidth: 0 },
  // Lighten the native date input's mm/dd/yyyy text to match the other placeholders.
  // The format text is drawn by the webkit datetime-edit pseudo-element, which
  // doesn't pick up the input's `color`, so target it directly.
  dateInput: {
    '& input::-webkit-datetime-edit': { color: tokens.colorNeutralForeground4 },
  },
  dash: { color: tokens.colorNeutralForeground3 },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: '4px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
})

/** One choice in the company or vendor dropdown: what the user reads, and the
    id the API filters on. */
export interface FilterOption {
  id: string
  name: string
}

interface Props {
  companies: FilterOption[]
  vendors: FilterOption[]
  /** Render the trigger as a compact icon button (header tool group) instead of
      a labeled button. The active-group count moves into the tooltip/label and
      the button goes brand-colored when any filter is set. */
  iconOnly?: boolean
  value: InvoiceFilter
  onChange: (f: InvoiceFilter) => void
}

export function FilterButton({ companies, vendors, iconOnly = false, value, onChange }: Props) {
  const s = useStyles()

  const activeGroups = [
    value.company,
    value.vendor,
    value.fromDate || value.toDate,
    value.minTotal || value.maxTotal,
  ].filter(Boolean).length
  const filterLabel = activeGroups > 0 ? `Filter (${activeGroups})` : 'Filter'

  function set<K extends keyof InvoiceFilter>(key: K, v: string) {
    onChange({ ...value, [key]: v })
  }

  // A company or vendor choice sets both halves: the name the popover shows and
  // the id the query uses.
  function pick(group: 'company' | 'vendor', name: string) {
    const options = group === 'company' ? companies : vendors
    const id = options.find((o) => o.name === name)?.id ?? ''
    onChange(group === 'company'
      ? { ...value, company: name, companyId: id }
      : { ...value, vendor: name, vendorId: id })
  }

  return (
    <Popover positioning="below-start" withArrow>
      <PopoverTrigger disableButtonEnhancement>
        {iconOnly ? (
          <Button
            appearance={activeGroups > 0 ? 'primary' : 'secondary'}
            icon={<FilterRegular />}
            aria-label={filterLabel}
            title={filterLabel}
          />
        ) : (
          <Button appearance={activeGroups > 0 ? 'primary' : 'secondary'} icon={<FilterRegular />}>
            Filter{activeGroups > 0 ? ` (${activeGroups})` : ''}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverSurface className={s.surface}>
        <Field label="Company">
          <Dropdown
            className={s.control}
            placeholder="All companies"
            selectedOptions={value.company ? [value.company] : []}
            value={value.company}
            onOptionSelect={(_, d) => pick('company', d.optionValue ?? '')}
          >
            <Option value="">All companies</Option>
            {companies.map((c) => (
              <Option key={c.name} value={c.name}>{c.name}</Option>
            ))}
          </Dropdown>
        </Field>
        <Field label="Vendor">
          <Dropdown
            className={s.control}
            placeholder="All vendors"
            selectedOptions={value.vendor ? [value.vendor] : []}
            value={value.vendor}
            onOptionSelect={(_, d) => pick('vendor', d.optionValue ?? '')}
          >
            <Option value="">All vendors</Option>
            {vendors.map((v) => (
              <Option key={v.name} value={v.name}>{v.name}</Option>
            ))}
          </Dropdown>
        </Field>
        <Field label="Date Range">
          <div className={s.rangePair}>
            <Input className={mergeClasses(s.rangeInput, s.dateInput)} type="date" aria-label="From" value={value.fromDate} onChange={(_, d) => set('fromDate', d.value)} />
            <span className={s.dash}>–</span>
            <Input className={mergeClasses(s.rangeInput, s.dateInput)} type="date" aria-label="To" value={value.toDate} onChange={(_, d) => set('toDate', d.value)} />
          </div>
        </Field>
        <Field label="Total Range">
          <div className={s.rangePair}>
            <Input className={s.rangeInput} type="number" contentBefore="$" aria-label="Minimum" placeholder="Min" value={value.minTotal} onChange={(_, d) => set('minTotal', d.value)} />
            <span className={s.dash}>–</span>
            <Input className={s.rangeInput} type="number" contentBefore="$" aria-label="Maximum" placeholder="Max" value={value.maxTotal} onChange={(_, d) => set('maxTotal', d.value)} />
          </div>
        </Field>
        <div className={s.footer}>
          <Button
            appearance="subtle"
            icon={<DismissRegular />}
            disabled={activeGroups === 0}
            onClick={() => onChange(emptyFilter)}
          >
            Clear all
          </Button>
        </div>
      </PopoverSurface>
    </Popover>
  )
}
